import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../admin/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Star } from "lucide-react";

const EMPTY_PLAN = () => ({
  id: `plan-${Date.now()}`,
  featured: false,
  name_de: "",
  name_en: "",
  name_es: "",
  price_de: "",
  price_en: "",
  price_es: "",
  unit_de: "",
  unit_en: "",
  unit_es: "",
  desc_de: "",
  desc_en: "",
  desc_es: "",
  features_de: [],
  features_en: [],
  features_es: [],
});

export default function AdminServiceModels() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState("de");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/content/settings");
        if (!mounted) return;
        if (Array.isArray(data.models) && data.models.length > 0) {
          setPlans(data.models);
        } else {
          // sensible defaults — three plans matching the public page
          setPlans([
            {
              ...EMPTY_PLAN(),
              id: "spot",
              name_de: "Spot-Einsatz", name_en: "Spot deployment", name_es: "Despliegue puntual",
              price_de: "ab CHF 990", price_en: "from CHF 990", price_es: "desde CHF 990",
              unit_de: "/ Tag", unit_en: "/ day", unit_es: "/ día",
              desc_de: "Einmalige Montage, Wartung oder Revision — kurzfristig, ohne Bindung.",
              desc_en: "One-off installation, maintenance or overhaul — short notice, no lock-in.",
              desc_es: "Montaje, mantenimiento o revisión únicos — corto plazo, sin compromiso.",
              features_de: ["Reaktion < 72 h", "1–6 Monteure", "Werkzeug & Logistik inkl.", "Tagesberichte"],
              features_en: ["Response < 72h", "1–6 fitters", "Tools & logistics included", "Daily reports"],
              features_es: ["Reacción < 72 h", "1–6 montadores", "Herramienta y logística incluidas", "Partes diarios"],
            },
            {
              ...EMPTY_PLAN(),
              id: "framework",
              featured: true,
              name_de: "Rahmenvertrag", name_en: "Framework contract", name_es: "Contrato marco",
              price_de: "Individuell", price_en: "Custom", price_es: "A medida",
              desc_de: "Wiederkehrende Wartungen oder geplante Revisionsfenster, fixe Stundensätze.",
              desc_en: "Recurring maintenance windows or planned overhauls, fixed hourly rates.",
              desc_es: "Mantenimientos recurrentes o ventanas de revisión planificadas, tarifa horaria fija.",
              features_de: ["Servicekalender", "Bevorzugte Verfügbarkeit", "Quartalsreporting", "Eskalationspfad 24/7"],
              features_en: ["Service calendar", "Priority availability", "Quarterly reporting", "24/7 escalation"],
              features_es: ["Calendario de servicio", "Disponibilidad prioritaria", "Reporte trimestral", "Escalado 24/7"],
            },
            {
              ...EMPTY_PLAN(),
              id: "embedded",
              name_de: "Stammteam", name_en: "Embedded crew", name_es: "Equipo embebido",
              price_de: "Auf Anfrage", price_en: "On request", price_es: "Consultar",
              desc_de: "Dauerhaft eingesetzte Monteure als Verlängerung Ihrer Service-Organisation.",
              desc_en: "Permanently embedded fitters acting as an extension of your service org.",
              desc_es: "Montadores embebidos de forma permanente como extensión de su organización de servicio.",
              features_de: ["Dediziertes Team", "OEM-Schulungen", "Internationale Reisebereitschaft", "Mehrsprachig: DE · EN · ES"],
              features_en: ["Dedicated team", "OEM training", "International travel", "DE · EN · ES"],
              features_es: ["Equipo dedicado", "Formación OEM", "Viajes internacionales", "DE · EN · ES"],
            },
          ]);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Laden");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const updatePlan = (idx, field, value) => {
    setPlans((arr) => arr.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const setFeatured = (idx) => {
    setPlans((arr) => arr.map((p, i) => ({ ...p, featured: i === idx })));
  };

  const addFeature = (idx, lang) => {
    const key = `features_${lang}`;
    setPlans((arr) =>
      arr.map((p, i) => (i === idx ? { ...p, [key]: [...(p[key] || []), ""] } : p))
    );
  };

  const updateFeature = (idx, lang, fi, value) => {
    const key = `features_${lang}`;
    setPlans((arr) =>
      arr.map((p, i) =>
        i === idx
          ? { ...p, [key]: p[key].map((f, j) => (j === fi ? value : f)) }
          : p
      )
    );
  };

  const removeFeature = (idx, lang, fi) => {
    const key = `features_${lang}`;
    setPlans((arr) =>
      arr.map((p, i) =>
        i === idx ? { ...p, [key]: p[key].filter((_, j) => j !== fi) } : p
      )
    );
  };

  const addPlan = () => {
    if (plans.length >= 6) return toast.error("Maximal 6 Pakete.");
    setPlans((arr) => [...arr, EMPTY_PLAN()]);
  };

  const removePlan = (idx) => {
    if (plans.length <= 1) return toast.error("Mindestens ein Paket erforderlich.");
    if (!window.confirm("Paket wirklich entfernen?")) return;
    setPlans((arr) => arr.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/models", { plans });
      toast.success("Servicemodelle gespeichert.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-[#5A6270] font-mono-tech">LÄDT…</div>;

  return (
    <div data-testid="admin-models" className="p-6 md:p-10 max-w-6xl">
      <div className="font-mono-tech text-[#1d428a] mb-2">
        <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
        ADMIN · SERVICEMODELLE
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#0A0A0A]">Servicemodelle.</h1>
      <p className="mt-3 text-[#5A6270] text-sm md:text-base max-w-2xl">
        Pakete, Preise und Leistungen — pro Sprache pflegbar. Änderungen erscheinen sofort auf der Webseite (Cache: ein Reload).
      </p>

      {/* Language tabs */}
      <div className="mt-8 flex items-center gap-1 border border-[#1d428a] w-fit">
        {["de", "en", "es"].map((l, i) => (
          <button
            key={l}
            type="button"
            onClick={() => setActiveLang(l)}
            data-testid={`admin-models-lang-${l}`}
            className={`font-mono-tech px-4 py-2 ${
              activeLang === l ? "bg-[#1d428a] text-white" : "text-[#1d428a] hover:bg-[#1d428a]/10"
            } ${i !== 2 ? "border-r border-[#1d428a]" : ""}`}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-6">
        {plans.map((p, idx) => (
          <div key={p.id || idx} className="bg-white border border-[#1d428a]/15">
            <div className="flex items-center justify-between p-4 border-b border-[#1d428a]/15 bg-[#e8ecf3]/50">
              <div className="font-mono-tech text-[#df4661]">PAKET {String(idx + 1).padStart(2, "0")}</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFeatured(idx)}
                  className={`inline-flex items-center gap-1 font-mono-tech px-3 py-1.5 transition-colors ${
                    p.featured ? "bg-[#df4661] text-white" : "border border-[#1d428a] text-[#1d428a] hover:bg-[#1d428a] hover:text-white"
                  }`}
                  title="Als Hauptpaket markieren"
                >
                  <Star size={13} fill={p.featured ? "currentColor" : "none"} />
                  {p.featured ? "EMPFOHLEN" : "ALS EMPFOHLEN"}
                </button>
                <button
                  type="button"
                  onClick={() => removePlan(idx)}
                  className="p-2 text-[#df4661] hover:bg-[#df4661] hover:text-white transition-colors"
                  title="Paket entfernen"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Name" value={p[`name_${activeLang}`]} onChange={(v) => updatePlan(idx, `name_${activeLang}`, v)} />
              <Field label="Preis" value={p[`price_${activeLang}`]} onChange={(v) => updatePlan(idx, `price_${activeLang}`, v)} />
              <Field label="Einheit" value={p[`unit_${activeLang}`]} onChange={(v) => updatePlan(idx, `unit_${activeLang}`, v)} placeholder="/ Tag, / plan, …" />
              <div className="md:col-span-3">
                <Field
                  label="Beschreibung"
                  value={p[`desc_${activeLang}`]}
                  onChange={(v) => updatePlan(idx, `desc_${activeLang}`, v)}
                  textarea
                />
              </div>

              <div className="md:col-span-3">
                <div className="font-mono-tech text-[#df4661] mb-2 mt-2">LEISTUNGSMERKMALE ({activeLang.toUpperCase()})</div>
                <div className="space-y-2">
                  {(p[`features_${activeLang}`] || []).map((f, fi) => (
                    <div key={fi} className="flex gap-2">
                      <input
                        value={f}
                        onChange={(e) => updateFeature(idx, activeLang, fi, e.target.value)}
                        className="flex-1 border border-[#1d428a]/30 px-3 py-2 text-sm outline-none focus:border-[#df4661]"
                        placeholder="z.B. Reaktion < 72h"
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature(idx, activeLang, fi)}
                        className="p-2 text-[#df4661] border border-[#df4661] hover:bg-[#df4661] hover:text-white transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addFeature(idx, activeLang)}
                    className="inline-flex items-center gap-2 font-mono-tech text-[#1d428a] hover:text-[#df4661]"
                  >
                    <Plus size={13} /> MERKMAL HINZUFÜGEN
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addPlan}
          className="inline-flex items-center gap-2 font-mono-tech border border-[#1d428a] text-[#1d428a] px-4 py-2.5 hover:bg-[#1d428a] hover:text-white transition-colors"
        >
          <Plus size={14} /> PAKET HINZUFÜGEN
        </button>
      </div>

      <div className="mt-8 sticky bottom-4">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-models-save"
          className="inline-flex items-center gap-3 bg-[#1d428a] text-white font-mono-tech px-6 py-4 hover:bg-[#df4661] disabled:opacity-60 transition-colors shadow-lg"
        >
          <Save size={15} />
          {saving ? "SPEICHERN…" : "ALLE ÄNDERUNGEN SPEICHERN"}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, textarea, placeholder }) => (
  <div>
    <label className="font-mono-tech text-[#5A6270] block mb-1">{label.toUpperCase()}</label>
    {textarea ? (
      <textarea
        rows={2}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#1d428a]/30 px-3 py-2 text-sm outline-none focus:border-[#df4661] resize-none"
        placeholder={placeholder}
      />
    ) : (
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#1d428a]/30 px-3 py-2 text-sm outline-none focus:border-[#df4661]"
        placeholder={placeholder}
      />
    )}
  </div>
);
